// 型だけを借りる（ビルド時に消える）。ホワイトリストの正はプロキシ側に置いたまま、
// 未登録の endpoint を渡すと typecheck で落ちるようにする。
import type { AllowedMicroCMSEndpoint } from '@/server/src/presentation/microCMS/proxyPath';

import type { MicroCMSListContent, MicroCMSListResponse, MicroCMSQueries } from 'microcms-js-sdk';

/**
 * microCMS 取得の Data Layer。createClient / nuxt-microcms-module はブラウザに API キーを渡すため使わず、
 * キーを持たないサーバープロキシ（`server/api/microcms/[...path].get.ts`）だけを叩く。
 *
 * Vue/Nuxt に依存しない。setup から使う入口は `wabunka-src/common/composables/query/useMicroCMSClient.ts`。
 */
const PROXY_BASE_PATH = '/api/microcms';

export interface MicroCMSListParams {
  endpoint: AllowedMicroCMSEndpoint;
  queries?: MicroCMSQueries;
}

export interface MicroCMSListDetailParams extends MicroCMSListParams {
  contentId: string;
}

/** fields / ids は配列でも渡されるため、microCMS が受け取れるカンマ区切りへ寄せる */
const normalizeQueries = (queries?: MicroCMSQueries) => {
  if (!queries) return undefined;

  const { fields, ids, ...rest } = queries;

  return {
    ...rest,
    ...(fields === undefined ? {} : { fields: fields.toString() }),
    ...(ids === undefined ? {} : { ids: ids.toString() }),
  };
};

/** fetch 手段に依らず必要なものだけを返す。useFetch のように path と query を別々に渡す呼び出しにも使える */
export const buildMicroCMSProxyRequest = ({ endpoint, contentId, queries }: MicroCMSListParams & { contentId?: string }) => {
  return {
    path: contentId === undefined ? `${PROXY_BASE_PATH}/${endpoint}` : `${PROXY_BASE_PATH}/${endpoint}/${contentId}`,
    query: normalizeQueries(queries),
  };
};

/** ofetch / useRequestFetch() のどちらも満たす最小の形。Nuxt に依存させないための注入点 */
type MicroCMSFetcher = <T>(path: string, options: { query?: Record<string, unknown> }) => Promise<T>;

/**
 * fetcher を注入して作る microCMS プロキシクライアント。戻り値の型は microcms-js-sdk の
 * createClient と同じ（getList: MicroCMSListResponse<T> / getListDetail: T & MicroCMSListContent）。
 */
export const createMicroCMSProxyClient = (fetcher: MicroCMSFetcher) => {
  const getList = <T>({ endpoint, queries }: MicroCMSListParams) => {
    const { path, query } = buildMicroCMSProxyRequest({ endpoint, queries });

    return fetcher<MicroCMSListResponse<T>>(path, { query });
  };

  const getListDetail = <T>({ endpoint, contentId, queries }: MicroCMSListDetailParams) => {
    const { path, query } = buildMicroCMSProxyRequest({ endpoint, contentId, queries });

    return fetcher<T & MicroCMSListContent>(path, { query });
  };

  return { getList, getListDetail };
};
